import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, FileText, Users, Trash2, Download, AlertCircle, CheckCircle, Plus } from 'lucide-react';
import { leadsListService } from '../lib/supabaseServices';

const LeadsImportModal = ({ isOpen, onClose, darkMode, onLeadsListSelected }) => {
  const [currentStep, setCurrentStep] = useState(0); // 0: Select/Create List, 1: Upload CSV
  const [leadsLists, setLeadsLists] = useState([]);
  const [selectedList, setSelectedList] = useState(null);
  const [newListName, setNewListName] = useState('');
  const [newListDescription, setNewListDescription] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState('');
  const [previewData, setPreviewData] = useState([]);
  const [importing, setImporting] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [importProgress, setImportProgress] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadLeadsLists();
    }
  }, [isOpen]);

  const loadLeadsLists = async () => {
    try {
      const lists = await leadsListService.getLeadsLists();
      setLeadsLists(lists);
    } catch (error) {
      console.error('Error loading leads lists:', error);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      setCsvFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target.result;
        setCsvData(content);
        
        // Preview first 5 usernames
        const lines = content.trim().split('\n');
        const usernames = lines
          .slice(1, 6) // Skip header, take first 5
          .map(line => line.split(',')[0].replace(/['"]/g, '').trim())
          .filter(username => username.length > 0);
        
        setPreviewData(usernames);
      };
      reader.readAsText(file);
    } else {
      alert('Please select a valid CSV file');
    }
  };

  const createNewList = async () => {
    try {
      const newList = await leadsListService.createLeadsList({
        name: newListName,
        description: newListDescription
      });
      setSelectedList(newList);
      setLeadsLists([newList, ...leadsLists]);
      setCurrentStep(1);
    } catch (error) {
      console.error('Error creating leads list:', error);
      alert('Failed to create leads list: ' + error.message);
    }
  };

  const selectExistingList = (list) => {
    setSelectedList(list);
    onLeadsListSelected(list); // Immediately return the selected list
    handleClose(); // Close the modal since we're done
  };

  const handleImport = async () => {
    if (!selectedList || !csvData) return;
    
    try {
      setImporting(true);
      setImportProgress('Processing CSV file...');
      
      // Count leads for progress display
      const lines = csvData.trim().split('\n').slice(1).filter(line => line.trim());
      const totalLeads = lines.length;
      setImportProgress(`Importing ${totalLeads} leads...`);
      
      await leadsListService.importLeadsFromCSV(selectedList.id, csvData);
      setUploadComplete(true);
      setImportProgress(`Successfully imported ${totalLeads} leads!`);
      
      // Refresh the list to get updated count
      const updatedLists = await leadsListService.getLeadsLists();
      const updatedList = updatedLists.find(l => l.id === selectedList.id);
      setSelectedList(updatedList);
      
    } catch (error) {
      console.error('Error importing leads:', error);
      setImportProgress('');
      alert('Failed to import leads: ' + error.message);
    } finally {
      setImporting(false);
    }
  };

  const handleComplete = () => {
    if (selectedList) {
      onLeadsListSelected(selectedList);
    }
    handleClose();
  };

  const handleClose = () => {
    setCurrentStep(0);
    setSelectedList(null);
    setNewListName('');
    setNewListDescription('');
    setCsvFile(null);
    setCsvData('');
    setPreviewData([]);
    setImporting(false);
    setUploadComplete(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={(e) => e.target === e.currentTarget && handleClose()}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className={`${
            darkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'
          } rounded shadow-2xl border max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col`}
        >
          {/* Header */}
          <div className={`${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-gray-50 border-gray-200'} px-6 py-4 border-b`}>
            <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Import Leads List
            </h2>
            <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-600'} mt-1`}>
              {currentStep === 0 ? 'Select or create a leads list' : 'Upload your CSV file with usernames'}
            </p>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <AnimatePresence mode="wait">
              {currentStep === 0 ? (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  {/* Create New List */}
                  <div className={`${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-gray-50 border-gray-200'} border rounded p-4`}>
                    <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-4`}>
                      Create New Leads List
                    </h3>
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={newListName}
                        onChange={(e) => setNewListName(e.target.value)}
                        placeholder="e.g., Real Estate Agents NYC"
                        className={`w-full px-3 py-2 border rounded focus:ring-2 focus:border-transparent ${
                          darkMode 
                            ? 'bg-surface border-border text-primary placeholder-secondary focus:ring-accent' 
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-accent'
                        }`}
                      />
                      <textarea
                        value={newListDescription}
                        onChange={(e) => setNewListDescription(e.target.value)}
                        placeholder="Optional description..."
                        rows={2}
                        className={`w-full px-3 py-2 border rounded focus:ring-2 focus:border-transparent ${
                          darkMode 
                            ? 'bg-surface border-border text-primary placeholder-secondary focus:ring-accent' 
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-accent'
                        }`}
                      />
                      <button
                        onClick={createNewList}
                        disabled={!newListName.trim()}
                        className={`w-full py-2 px-4 rounded font-medium transition-colors ${
                          !newListName.trim()
                            ? darkMode ? 'bg-slate-700 text-slate-500' : 'bg-gray-200 text-gray-400'
                            : darkMode ? 'bg-accent hover:bg-accent-hover text-primary' : 'bg-accent hover:bg-accent-hover text-primary'
                        }`}
                      >
                        Create & Continue
                      </button>
                    </div>
                  </div>

                  {/* Or Select Existing */}
                  {leadsLists.length > 0 && (
                    <div>
                      <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-4`}>
                        Or Select Existing List
                      </h3>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {leadsLists.map((list) => (
                          <div
                            key={list.id}
                            onClick={() => selectExistingList(list)}
                            className={`p-3 border rounded cursor-pointer transition-colors ${
                              darkMode
                                ? 'border-slate-600 bg-slate-800 hover:border-slate-500 hover:bg-slate-700'
                                : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                  {list.name}
                                </h4>
                                {list.description && (
                                  <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                                    {list.description}
                                  </p>
                                )}
                              </div>
                              <div className="text-right">
                                <div className={`text-sm font-medium ${darkMode ? 'text-accent' : 'text-accent'}`}>
                                  {list.total_leads} leads
                                </div>
                                <div className={`text-xs ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                                  {new Date(list.created_at).toLocaleDateString()}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </motion.div>
              ) : (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  {/* Selected List Info */}
                  <div className={`${darkMode ? 'bg-surface border-border' : 'bg-accent/10 border-accent/30'} border rounded p-4`}>
                    <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-2`}>
                      Selected List: {selectedList?.name}
                    </h3>
                    <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                      Current leads: {selectedList?.total_leads || 0}
                    </p>
                  </div>

                  {!uploadComplete ? (
                    <>
                      {/* CSV Format Info */}
                      <div className={`${darkMode ? 'bg-amber-500/10 border-amber-500/20' : 'bg-yellow-50 border-yellow-200'} border rounded p-4`}>
                        <div className="flex items-start space-x-3">
                          <AlertCircle className={`w-5 h-5 ${darkMode ? 'text-amber-400' : 'text-yellow-600'} mt-0.5`} />
                          <div>
                            <h4 className={`font-medium ${darkMode ? 'text-amber-400' : 'text-yellow-800'} mb-2`}>
                              CSV Format Requirements
                            </h4>
                            <div className={`text-sm ${darkMode ? 'text-amber-300' : 'text-yellow-700'}`}>
                              <p className="mb-2">Your CSV should have usernames in the first column:</p>
                              <div className={`${darkMode ? 'bg-slate-900 border-slate-600' : 'bg-white border-gray-200'} border rounded p-2 font-mono text-xs`}>
                                Username<br/>
                                4bellsnabeau<br/>
                                4d_real_estate<br/>
                                505_realtor<br/>
                                ...
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* File Upload */}
                      <div>
                        <label className={`block text-sm font-medium ${darkMode ? 'text-slate-300' : 'text-gray-700'} mb-2`}>
                          Upload CSV File
                        </label>
                        <div className={`border-2 border-dashed rounded p-6 text-center transition-colors ${
                          darkMode 
                            ? 'border-slate-600 hover:border-slate-500' 
                            : 'border-gray-300 hover:border-gray-400'
                        }`}>
                          <Upload className={`w-12 h-12 ${darkMode ? 'text-slate-400' : 'text-gray-400'} mx-auto mb-4`} />
                          <input
                            type="file"
                            accept=".csv"
                            onChange={handleFileUpload}
                            className="hidden"
                            id="csvUpload"
                          />
                          <label 
                            htmlFor="csvUpload"
                            className={`${darkMode ? 'bg-accent hover:bg-accent-hover' : 'bg-accent hover:bg-accent-hover'} text-primary px-6 py-2 rounded font-medium cursor-pointer transition-colors`}
                          >
                            Choose CSV File
                          </label>
                          <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-600'} mt-2`}>
                            {csvFile ? csvFile.name : 'No file selected'}
                          </p>
                        </div>
                      </div>

                      {/* Preview */}
                      {previewData.length > 0 && (
                        <div className={`${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-gray-50 border-gray-200'} border rounded p-4`}>
                          <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'} mb-3`}>
                            Preview (first 5 usernames)
                          </h4>
                          <div className="space-y-1">
                            {previewData.map((username, index) => (
                              <div key={index} className={`text-sm ${darkMode ? 'text-slate-300' : 'text-gray-700'} font-mono`}>
                                @{username}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className={`${darkMode ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-green-50 border-green-200'} border rounded p-6 text-center`}>
                      <CheckCircle className={`w-16 h-16 ${darkMode ? 'text-emerald-400' : 'text-green-500'} mx-auto mb-4`} />
                      <h3 className={`text-lg font-semibold ${darkMode ? 'text-emerald-400' : 'text-green-800'} mb-2`}>
                        Import Successful!
                      </h3>
                      <p className={`${darkMode ? 'text-emerald-300' : 'text-green-700'}`}>
                        Your leads have been imported to "{selectedList?.name}"
                      </p>
                      <p className={`text-sm ${darkMode ? 'text-emerald-400' : 'text-green-600'} mt-2`}>
                        Total leads: {selectedList?.total_leads}
                      </p>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer */}
          <div className={`${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-gray-50 border-gray-200'} px-6 py-4 border-t flex items-center justify-between`}>
            <button
              onClick={currentStep === 0 ? handleClose : () => setCurrentStep(0)}
              className={`px-4 py-2 rounded transition-colors ${
                darkMode ? 'text-slate-300 hover:text-white hover:bg-slate-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              {currentStep === 0 ? 'Cancel' : 'Back'}
            </button>

            {currentStep === 1 && (
              <div className="flex space-x-3">
                {!uploadComplete ? (
                  <button
                    onClick={handleImport}
                    disabled={!csvData || importing}
                    className={`px-6 py-2 rounded font-medium transition-colors ${
                      !csvData || importing
                        ? darkMode ? 'bg-slate-700 text-slate-500' : 'bg-gray-200 text-gray-400'
                        : darkMode ? 'bg-accent hover:bg-accent-hover text-primary' : 'bg-accent hover:bg-accent-hover text-primary'
                    }`}
                  >
                    {importing ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>{importProgress || 'Importing...'}</span>
                      </div>
                    ) : (
                      'Import Leads'
                    )}
                  </button>
                ) : (
                  <button
                    onClick={handleComplete}
                    className={`px-6 py-2 rounded font-medium transition-colors ${
                      darkMode ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : 'bg-green-500 hover:bg-green-600 text-white'
                    }`}
                  >
                    Use This List
                  </button>
                )}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default LeadsImportModal;